import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@repletoryreach/sdk-auth';
import { useRealtime } from '@repletoryreach/sdk-realtime';
import { initials } from '@repletoryreach/sdk-utils/format';
import { Badge } from '@repletoryreach/sdk-ui';
import { ScrollArea } from '@repletoryreach/sdk-ui/scroll';
import { Select } from '@repletoryreach/sdk-ui/inputs';
import { NAV, activeNavPath } from '../../lib/nav';
import { useVoiceStatus } from '../../lib/voice';
import { useIsOrgAdmin } from '../components/RoleGate';
import { NavIcon } from './nav-icons';
import { useI18n } from '../../i18n/I18nProvider';
import { useCredits } from '../../lib/credits';
import { LOCALES, type Locale } from '../../i18n/catalog';
import { localeCoverage } from '../../i18n/coverage';
import { BrandLogo } from '../../components/BrandLogo';
import { BrandWordmark } from '../../components/BrandWordmark';
import { useWallet } from '../../data/useWallet';
import { walletState, shownBalance } from '../../lib/wallet';
import { formatMinor } from '../../lib/format';

export interface SidebarProps {
  /** `drawer`, mounted inside the ≤768px off-canvas Drawer (brand row lives in the drawer title). */
  variant?: 'sidebar' | 'drawer';
  /** Fires after a nav item is chosen: the mobile shell closes its drawer. */
  onNavigate?: () => void;
  /** Per-item counts keyed by nav path, e.g. `{ '/inbox': 3 }`. Rendered as a badge. */
  badges?: Record<string, number>;
}

export function Sidebar({ variant = 'sidebar', onNavigate, badges }: SidebarProps = {}) {
  const { locale, setLocale, t } = useI18n();
  const credits = useCredits();
  const { pathname } = useLocation();
  const { available: voiceAvailable } = useVoiceStatus();
  // Agents see the inbox and the contact book (roles, 2 October 2026).
  const isAdmin = useIsOrgAdmin();
  const { user } = useAuth();
  const { status } = useRealtime();
  const inDrawer = variant === 'drawer';

  // The balance belongs in the chrome: a tenant who runs out mid-broadcast
  // finds out from a failed send otherwise, and the wallet was two clicks
  // away on a page nobody visits until something breaks.
  const { data: walletRes, isPending: walletPending } = useWallet();
  const wallet = walletRes?.data;
  const walletTone = wallet ? walletState(wallet.credits_minor, wallet.currency, wallet.state, wallet.extra_use) : 'ok';
  // Extra use carrying on after the credit ran out (30 October 2026): what it has reached this month, not a
  // negative balance.
  const extra = walletTone === 'extra' && wallet?.extra_use ? wallet.extra_use : null;
  const walletAmount = wallet
    ? extra
      ? t('billing.extra.chipAmount', { used: formatMinor(extra.used_minor, wallet.currency), limit: formatMinor(extra.limit_minor, wallet.currency) })
      : credits(shownBalance(wallet.credits_minor, wallet.extra_use), wallet.currency)
    : '';

  // The dot beside the name reports the live connection, not a made-up
  // presence: "Online" under a dropped socket would be a lie.
  const rt = status === 'connected' ? 'connected'
    : status === 'connecting' ? 'connecting'
    : 'disconnected';

  return (
    <aside className={`rr-shell__sidebar${inDrawer ? ' rr-shell__sidebar--drawer' : ''}`}>
      {!inDrawer && (
        <div className="rr-shell__brand">
          <BrandLogo size={34} className="rr-shell__brand-mark" />
          <BrandWordmark className="rr-shell__brand-name" />
        </div>
      )}

      <ScrollArea as="nav" data-guide="shell-menu" className="rr-shell__nav" viewportClassName="rr-shell__nav-viewport" axis="y" overscroll="contain" aria-label={t('nav.menu')}>
        {(() => {
          // Every item the menu could show, so the page's own item wins over its parent (Compose over Inbox).
          const current = activeNavPath(pathname, NAV.flatMap((sct) => sct.items.map((i) => i.path)));
          return NAV.map((section) => (
          <div key={section.label} className="rr-shell__nav-section">
            <div className="rr-shell__nav-title">{t(section.labelKey, section.label)}</div>
            {section.items.filter((item) => (item.feature !== 'voice' || voiceAvailable) && (!item.adminOnly || isAdmin)).map((item) => {
              const active = item.path === current;
              const count = badges?.[item.path] ?? 0;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`rr-shell__nav-link${active ? ' is-active' : ''}`}
                  onClick={onNavigate}
                  aria-current={active ? 'page' : undefined}
                >
                  <NavIcon name={item.icon} />
                  <span className="rr-shell__nav-label">{t(item.labelKey, item.label)}</span>
                  {count > 0 && (
                    <Badge size="xs" variant="filled" className="rr-shell__nav-badge">
                      {count > 99 ? '99+' : count}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </div>
          ));
        })()}
      </ScrollArea>

      <div className="rr-shell__sidebar-foot">
        <Link
          to="/billing"
          className={`rr-shell__wallet is-${walletTone}`}
          data-guide="shell-wallet"
          onClick={onNavigate}
          aria-label={extra ? `${t('billing.extra.chipLabel')}: ${walletAmount}` : t('shell.wallet.aria', { amount: walletAmount })}
        >
          <span className="rr-shell__wallet-icon" aria-hidden><WalletIcon /></span>
          <span className="rr-shell__wallet-text">
            {/*
              The caption doubles as the state. A badge alongside it wrapped
              "Wallet balance" onto two lines in a 248px rail, and the row is
              worth exactly two lines whatever the balance is. It stays text
              rather than colour alone, so the state survives a greyscale
              screen as well as the amber rule does.
            */}
            <span className="rr-shell__wallet-label">
              {walletTone === 'empty' ? t('billing.emptyBalance')
                : walletTone === 'low' ? t('billing.lowBalance')
                : walletTone === 'extra' ? t('billing.extra.chipLabel')
                : t('billing.walletBalance')}
            </span>
            <span className="rr-shell__wallet-amount">
              {walletPending ? '…' : wallet ? walletAmount : '–'}
            </span>
          </span>
          <ChevronIcon />
        </Link>

        {/*
          Who you are signed in as, at the foot of the rail, per the design
          reference. It matters beyond decoration: with impersonation in the
          product this is the only persistent statement of WHICH account the
          console is acting for.
        */}
        {user && (
          <Link to="/settings/profile" className="rr-shell__me" onClick={onNavigate}>
            <span className="rr-shell__me-avatar" aria-hidden>{initials(user.display_name || user.email)}</span>
            <span className="rr-shell__me-text">
              <span className="rr-shell__me-name">{user.display_name || user.email}</span>
              <span className="rr-shell__me-status">
                <span className={`rr-shell__me-dot rr-shell__me-dot--${rt}`} aria-hidden />
                {t(`shell.realtime.${rt}`)}
              </span>
            </span>
            <ChevronIcon />
          </Link>
        )}

        <div className="rr-shell__locale-row" data-guide="shell-language">
        <span className="rr-shell__locale-icon" aria-hidden><GlobeIcon /></span>
        <Select
          size="xs"
          className="rr-shell__locale-select"
          aria-label={t('shell.localePicker')}
          value={locale}
          onChange={(e) => setLocale(e.currentTarget.value as Locale)}
          /*
            Partly-translated languages used to look identical to complete ones,
            so choosing isiZulu silently left a third of the console in English
            (_UI_improvements/tenant/_app-shell.md §4 item 7). The percentage is
            computed from the catalogs, so it cannot drift.
          */
          data={LOCALES.map((l) => {
            const pct = localeCoverage(l.code);
            return {
              value: l.code,
              label: pct === 100 ? l.label : t('shell.locale.partial', { label: l.label, percent: pct })
            };
          })}
        />
        </div>
      </div>
    </aside>
  );
}

function ChevronIcon() {
  return (
    <svg className="rr-shell__me-chevron" width="14" height="14" viewBox="0 0 16 16" fill="none"
      stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="4,10 8,6 12,10" />
    </svg>
  );
}

function WalletIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="2.5" y="4.5" width="15" height="11" rx="2.2" />
      <path d="M2.5 8.5h15" />
      <circle cx="14.2" cy="12" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="8" cy="8" r="6" />
      <path d="M2 8h12M8 2a9 9 0 010 12M8 2a9 9 0 000 12" />
    </svg>
  );
}
